import { createFileRoute } from '@tanstack/react-router';
import type React from 'react';
import TablerIconArrowDown from '~icons/tabler/arrow-down';
import TablerIconArrowRight from '~icons/tabler/arrow-right';

import { Button, Grid, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSection, PageSectionTitle } from '#/components/pages';
import fx from '#/fx';
import { Content, type ExtractLoaderData } from '#/lib/content';
import { canonicalLinkTag, seo } from '#/lib/head';

export const Route = createFileRoute('/_main/ducks')({
  loader: async () => {
    const page = await load();
    return { page };
  },
  head: ({ loaderData, match }) => {
    return {
      meta: seo({
        title: loaderData?.page.seo.title ?? 'Whats up with the ducks?',
        description:
          loaderData?.page.seo.description ??
          'Joggr believes in the power of rubber duckies. Find out why.',
        url: match.pathname,
      }),
      links: [canonicalLinkTag(match.pathname)],
    };
  },
  component: DucksPage,
});

async function load() {
  return await fx.getPageContent({
    data: {
      key: 'ducks',
    },
  });
}

type LoaderData = ExtractLoaderData<typeof load>;

function DucksPage() {
  const { page } = Route.useLoaderData();
  return (
    <Page>
      <PageSectionHero sections={page.sections} />
      <PageSectionMeetTheDucks sections={page.sections} />
      <PageSectionCta sections={page.sections} />
    </Page>
  );
}

function PageSectionHero({ sections }: DucksPageSectionProps) {
  return (
    <PageSection>
      <div className='mx-auto w-fit h-fit mb-8'>
        <FloatingOnWater>
          <Img
            location='static'
            src={sections.hero.icon.src}
            height={sections.hero.icon.height}
            width={sections.hero.icon.width}
            alt='Rubber duck'
            responsive
          />
        </FloatingOnWater>
      </div>
      <div className='text-center'>
        <Typography variant='h1'>
          <Content content={sections.hero.title} />
        </Typography>
        <Typography variant='subtitle1' className='mx-auto md:max-w-3/4'>
          <Content content={sections.hero.subtitle} />
        </Typography>
        <Button
          variant='contained'
          startIcon={<TablerIconArrowDown />}
          onClick={() => {
            const el = document.getElementById('meet-the-ducks');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          size='large'
          className='mt-8 mx-auto text-center cursor-duck'
        >
          Meet the ducks
        </Button>
      </div>
    </PageSection>
  );
}

function PageSectionMeetTheDucks({ sections }: DucksPageSectionProps) {
  return (
    <PageSection id='meet-the-ducks'>
      <PageSectionTitle title={sections.meet.title} subtitle={sections.meet.subtitle} />
      <Grid container spacing={{ lg: 8 }} rowSpacing={8} className='items-center justify-center'>
        {sections.meet.ducks.map((duck) => (
          <Grid key={duck.name} size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
            <div className='max-w-[300px] md:w-[300px] mx-auto'>
              <GlowCard
                gutter='xl'
                className='max-h-[300px] md:h-[300px] hover:animate-shine cursor-duck flex flex-row items-center content-center mb-4 mx-auto'
              >
                <Img
                  location='static'
                  src={duck.image.src}
                  height={duck.image.height}
                  width={duck.image.width}
                  alt={duck.name}
                  className='max-w-[200px] mx-auto'
                  responsive
                />
              </GlowCard>
              <Stack spacing={1} className='text-center md:h-[100px]'>
                <Typography variant='h4' component='h3'>
                  <Content content={duck.name} />
                </Typography>
                <Typography variant='body1'>
                  <Content content={duck.bio} />
                </Typography>
              </Stack>
            </div>
          </Grid>
        ))}
      </Grid>
    </PageSection>
  );
}

function PageSectionCta({ sections }: DucksPageSectionProps) {
  const { title, ctas } = sections.callToAction;
  return (
    <PageSection>
      <GlowCard border='top' className='text-center p-4 md:p-8'>
        <Typography variant='h3' component='h3' className='text-white mb-16'>
          <Content content={title} />
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent='center'>
          <ButtonLink
            variant='contained'
            to={ctas.primary.url}
            endIcon={<TablerIconArrowRight />}
            size='large'
          >
            {ctas.primary.label}
          </ButtonLink>
          {ctas.secondary && (
            <ButtonLink variant='outlined' to={ctas.secondary.url} size='large'>
              {ctas.secondary.label}
            </ButtonLink>
          )}
        </Stack>
      </GlowCard>
    </PageSection>
  );
}

/*
|------------------
| Internals
|------------------
*/

type DucksPageSectionProps = Pick<LoaderData, 'sections'>;

function FloatingOnWater({ children }: React.PropsWithChildren) {
  const width = 1200;
  const height = 600;
  return (
    <div className='relative w-48 h-48 sm:w-72 sm:h-72 overflow-clip rounded-full border-solid border-4 border-gray-400 mx-auto'>
      <div className='absolute top-8 left-8 sm:top-16 sm:left-14 z-0'>
        <div className='animate-[wiggle_5s_linear_infinite] max-w-[124px] sm:max-w-[200px] mx-auto'>
          {children}
        </div>
      </div>
      <svg
        className='absolute left-0 top-16 sm:top-28 right-0 z-10 w-full h-full scale-125'
        width={`${width}`}
        height={`${height}`}
        viewBox={`0 0 ${width} ${height}`}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        role='presentation'
      >
        <path
          className='animate-[wiggle_7s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M0 0L33.7252 14.6224C67.4504 29.4424 134.901 58.6873 202.351 70.5433C269.802 82.2017 337.865 76.4713 405.316 67.5793C472.766 58.6873 540.216 47.0289 607.667 38.1368C675.117 29.4424 742.567 23.5144 810.018 38.1368C877.468 52.9569 945.532 88.1297 1012.98 91.0937C1080.43 94.0577 1147.88 64.6153 1215.33 58.6873C1282.78 52.9569 1350.23 70.5433 1417.68 73.5073C1485.14 76.4713 1553.2 64.6153 1620.65 55.7233C1688.1 47.0289 1755.55 41.1008 1789.27 38.1368L1823 35.1728V176.259H1789.27C1755.55 176.259 1688.1 176.259 1620.65 176.259C1553.2 176.259 1485.14 176.259 1417.68 176.259C1350.23 176.259 1282.78 176.259 1215.33 176.259C1147.88 176.259 1080.43 176.259 1012.98 176.259C945.532 176.259 877.468 176.259 810.018 176.259C742.567 176.259 675.117 176.259 607.667 176.259C540.216 176.259 472.766 176.259 405.316 176.259C337.865 176.259 269.802 176.259 202.351 176.259C134.901 176.259 67.4504 176.259 33.7252 176.259H0V0Z'
          fill='#005289'
        />
        <path
          className='animate-[wiggle_7s_linear_infinite]'
          d='M0 176H1823V504H0V176Z'
          fill='#005289'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M0 86.8809L23.301 89.9832C46.6021 93.0855 93.2042 99.2902 140.419 114.595C187.022 130.107 233.624 154.512 280.226 160.716C327.441 166.921 374.043 154.512 420.645 142.309C467.247 130.107 514.462 117.697 561.065 133.002C607.667 148.514 654.269 191.533 700.871 194.635C748.086 197.738 794.688 160.716 841.29 157.614C887.892 154.512 935.108 185.328 981.71 182.226C1028.31 179.124 1074.91 142.309 1122.13 120.8C1168.73 99.2902 1215.33 93.0855 1261.94 96.1879C1308.54 99.2902 1355.75 111.493 1402.35 123.902C1448.96 136.105 1495.56 148.514 1542.77 142.309C1589.38 136.105 1635.98 111.493 1682.58 123.902C1729.8 136.105 1776.4 185.328 1799.7 209.94L1823 234.552H1799.7C1776.4 234.552 1729.8 234.552 1682.58 234.552C1635.98 234.552 1589.38 234.552 1542.77 234.552C1495.56 234.552 1448.96 234.552 1402.35 234.552C1355.75 234.552 1308.54 234.552 1261.94 234.552C1215.33 234.552 1168.73 234.552 1122.13 234.552C1074.91 234.552 1028.31 234.552 981.71 234.552C935.108 234.552 887.892 234.552 841.29 234.552C794.688 234.552 748.086 234.552 700.871 234.552C654.269 234.552 607.667 234.552 561.065 234.552C514.462 234.552 467.247 234.552 420.645 234.552C374.043 234.552 327.441 234.552 280.226 234.552C233.624 234.552 187.022 234.552 140.419 234.552C93.2042 234.552 46.6021 234.552 23.301 234.552H0V86.8809Z'
          fill='#32ADFF'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          d='M0 234H1823V504H0V234Z'
          fill='#32ADFF'
        />
        <path
          className='animate-[wiggle_8s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M1823 84.335L1799.7 87.4908C1776.4 90.6466 1729.8 96.9582 1682.58 112.527C1635.98 128.306 1589.38 153.132 1542.77 159.444C1495.56 165.755 1448.96 153.132 1402.35 140.719C1355.75 128.306 1308.54 115.683 1261.94 131.252C1215.33 147.031 1168.73 190.791 1122.13 193.947C1074.91 197.103 1028.31 159.444 981.71 156.288C935.108 153.132 887.892 184.48 841.29 181.324C794.688 178.168 748.086 140.719 700.871 118.839C654.269 96.9582 607.667 90.6466 561.065 93.8024C514.462 96.9582 467.247 109.371 420.645 121.994C374.043 134.407 327.441 147.031 280.226 140.719C233.624 134.407 187.022 109.371 140.419 121.994C93.2042 134.407 46.6021 184.48 23.301 209.516L0 234.552H23.301C46.6021 234.552 93.2042 234.552 140.419 234.552C187.022 234.552 233.624 234.552 280.226 234.552C327.441 234.552 374.043 234.552 420.645 234.552C467.247 234.552 514.462 234.552 561.065 234.552C607.667 234.552 654.269 234.552 700.871 234.552C748.086 234.552 794.688 234.552 841.29 234.552C887.892 234.552 935.108 234.552 981.71 234.552C1028.31 234.552 1074.91 234.552 1122.13 234.552C1168.73 234.552 1215.33 234.552 1261.94 234.552C1308.54 234.552 1355.75 234.552 1402.35 234.552C1448.96 234.552 1495.56 234.552 1542.77 234.552C1589.38 234.552 1635.98 234.552 1682.58 234.552C1729.8 234.552 1776.4 234.552 1799.7 234.552H1823V84.335Z'
          fill='#1E74AE'
        />
        <path
          className='animate-[wiggle_8s_linear_infinite]'
          d='M3.40343e-05 234H1823V504H3.40343e-05V234Z'
          fill='#1E74AE'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          d='M0 241H1823V504H0V241Z'
          fill='#1E74AE'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M1823 91.335L1799.7 94.4908C1776.4 97.6466 1729.8 103.958 1682.58 119.527C1635.98 135.306 1589.38 160.132 1542.77 166.444C1495.56 172.755 1448.96 160.132 1402.35 147.719C1355.75 135.306 1308.54 122.683 1261.94 138.252C1215.33 154.031 1168.73 197.791 1122.13 200.947C1074.91 204.103 1028.31 166.444 981.71 163.288C935.108 160.132 887.892 191.48 841.29 188.324C794.688 185.168 748.086 147.719 700.871 125.839C654.269 103.958 607.667 97.6466 561.065 100.802C514.462 103.958 467.247 116.371 420.645 128.994C374.043 141.407 327.441 154.031 280.226 147.719C233.624 141.407 187.022 116.371 140.419 128.994C93.2042 141.407 46.6021 191.48 23.301 216.516L0 241.552H23.301C46.6021 241.552 93.2042 241.552 140.419 241.552C187.022 241.552 233.624 241.552 280.226 241.552C327.441 241.552 374.043 241.552 420.645 241.552C467.247 241.552 514.462 241.552 561.065 241.552C607.667 241.552 654.269 241.552 700.871 241.552C748.086 241.552 794.688 241.552 841.29 241.552C887.892 241.552 935.108 241.552 981.71 241.552C1028.31 241.552 1074.91 241.552 1122.13 241.552C1168.73 241.552 1215.33 241.552 1261.94 241.552C1308.54 241.552 1355.75 241.552 1402.35 241.552C1448.96 241.552 1495.56 241.552 1542.77 241.552C1589.38 241.552 1635.98 241.552 1682.58 241.552C1729.8 241.552 1776.4 241.552 1799.7 241.552H1823V91.335Z'
          fill='#1E74AE'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M0 155.706L50.1325 166.64C101.784 177.574 202.049 199.442 303.833 199.442C405.618 199.442 505.883 177.574 607.667 183.041C709.451 188.508 809.716 221.31 911.5 232.244C1013.28 243.178 1113.55 232.244 1215.33 199.442C1317.12 166.64 1417.38 140.335 1519.17 113C1620.95 85.6649 1688.5 113 1772.87 84.6349L1823 57.2998V352.519H1772.87C1721.22 352.519 1620.95 352.519 1519.17 352.519C1417.38 352.519 1317.12 352.519 1215.33 352.519C1113.55 352.519 1013.28 352.519 911.5 352.519C809.716 352.519 709.451 352.519 607.667 352.519C505.883 352.519 405.618 352.519 303.833 352.519C202.049 352.519 101.784 352.519 50.1325 352.519H0V155.706Z'
          fill='#128BDC'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          d='M0 352H1823V504H0V352Z'
          fill='#128BDC'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          fillRule='evenodd'
          clipRule='evenodd'
          d='M1823 176.26L1799.7 179.963C1776.4 183.666 1729.8 191.071 1682.58 209.339C1635.98 227.854 1589.38 256.984 1542.77 264.389C1495.56 271.795 1448.96 256.984 1402.35 242.419C1355.75 227.854 1308.54 213.042 1261.94 231.31C1215.33 249.825 1168.73 301.172 1122.13 304.875C1074.91 308.578 1028.31 264.389 981.71 260.687C935.108 256.984 887.892 293.766 841.29 290.063C794.688 286.36 748.086 242.419 700.871 216.745C654.269 191.071 607.667 183.666 561.064 187.369C514.462 191.071 467.247 205.636 420.645 220.448C374.043 235.013 327.441 249.825 280.226 242.419C233.624 235.013 187.021 205.636 140.419 220.448C93.204 235.013 46.6019 293.766 23.3009 323.143L0 352.519H23.3009C46.6019 352.519 93.204 352.519 140.419 352.519C187.021 352.519 233.624 352.519 280.226 352.519C327.441 352.519 374.043 352.519 420.645 352.519C467.247 352.519 514.462 352.519 561.064 352.519C607.667 352.519 654.269 352.519 700.871 352.519C748.086 352.519 794.688 352.519 841.29 352.519C887.892 352.519 935.108 352.519 981.71 352.519C1028.31 352.519 1074.91 352.519 1122.13 352.519C1168.73 352.519 1215.33 352.519 1261.94 352.519C1308.54 352.519 1355.75 352.519 1402.35 352.519C1448.96 352.519 1495.56 352.519 1542.77 352.519C1589.38 352.519 1635.98 352.519 1682.58 352.519C1729.8 352.519 1776.4 352.519 1799.7 352.519H1823V176.26Z'
          fill='#0B65A1'
        />
        <path
          className='animate-[wiggle_5s_linear_infinite]'
          d='M0 352H1823V504H0V352Z'
          fill='#0B65A1'
        />
      </svg>
    </div>
  );
}
